import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode
} from 'react';

import { ISaleData, IShipping } from '~types/main';
import { safeJsonParse } from '@/src/utils/storage';

type SaleContextData = {
  sale: ISaleData;
  setSale: (sale: ISaleData) => void;
  updateSale: (sale: Partial<ISaleData>) => void;
  shippingCompany: IShipping;
  setShippingCompany: (shippingCompany: IShipping) => void;
  updateShippingCompany: (shippingCompany: Partial<IShipping>) => void;
}

const SaleContext = createContext<SaleContextData>({} as SaleContextData);

type ProviderProps = {
  children: ReactNode;
}

export function SaleProvider({ children }: ProviderProps) {
  const [currentSale, setCurrentSale] = useState<ISaleData>(() => {
    const sale = localStorage.getItem('@auge:sale');
    return safeJsonParse<ISaleData>(sale, {} as ISaleData);
  });

  const [currentShippingCompany, setCurrentShippingCompany] = useState<IShipping>(() => {
    const shippingCompany = localStorage.getItem('@auge:shippingCompany');
    return safeJsonParse<IShipping>(shippingCompany, {} as IShipping);
  });

  const updateSale = useCallback((sale: Partial<ISaleData>) => {
    setCurrentSale(prev => {
      const updatedSale = { ...prev, ...sale };
      localStorage.setItem('@auge:sale', JSON.stringify(updatedSale));
      return updatedSale;
    });
  }, []);

  const handleSetSale = useCallback((updatedSale: ISaleData) => {
    localStorage.setItem('@auge:sale', JSON.stringify(updatedSale));
    setCurrentSale(updatedSale);
  }, []);

  const updateShippingCompany = useCallback((shippingCompany: Partial<IShipping>) => {
    setCurrentShippingCompany(prev => {
      const updatedShippingCompany = { ...prev, ...shippingCompany };
      localStorage.setItem('@auge:shippingCompany', JSON.stringify(updatedShippingCompany));
      return updatedShippingCompany;
    });
  }, []);

  const handleSetShippingCompany = useCallback((updatedShippingCompany: IShipping) => {
    localStorage.setItem('@auge:shippingCompany', JSON.stringify(updatedShippingCompany));
    setCurrentShippingCompany(updatedShippingCompany);
  }, []);

  return (
    <SaleContext.Provider
      value={{
        sale: currentSale,
        setSale: handleSetSale,
        updateSale,
        shippingCompany: currentShippingCompany,
        setShippingCompany: handleSetShippingCompany,
        updateShippingCompany,
      }}
    >
      {children}
    </SaleContext.Provider>
  );
}

export function useSale() {
  const context = useContext(SaleContext);

  if (!context) {
    throw new Error("useSale must be used within a SaleProvider.");
  }

  return context;
}
